import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router";
import { userLogin } from "../Services/Services";
import {
  storeToken,
  storeResponse,
  removeToken,
  removeDivision,
} from "../Services/LocalStorageService";
import { startTokenWatcher } from "../Services/tokenHelper";
import image1 from "./Image/bg.jpg";
import image2 from "./Image/bg.jpg";
import image3 from "./Image/logoblack.png";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function LoginPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

 const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("⚠️ Please fill in both email and password.");
      toast.warning("⚠️ Please fill in both email and password.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await userLogin({ email, password });

      if (response?.access) {
        removeToken();
        storeToken(response.access);
        storeResponse(response.user);
       removeDivision();

        const permissionKey = `user_permissions_${response.user.id}`;
        if (!localStorage.getItem(permissionKey)) {
          localStorage.setItem(permissionKey, JSON.stringify([]));
        }
        toast.success("Login successful!");
        setTimeout(() => {
          startTokenWatcher();
          navigate("/division-cards", { replace: true });
        }, 1500);
      } else {
        setError("❌ Invalid credentials. Please try again.");
        toast.error("❌ Invalid credentials. Please try again.");
      }
    } catch (err) {
      console.error("❌ Login error:", err);
      const detail = err.response?.data?.detail;
      if (detail?.toLowerCase().includes("inactive")) {
        setError("❌ User is inactive. Please contact admin.");
        toast.error("❌ User is inactive. Please contact admin.");
      } else if (detail?.toLowerCase().includes("invalid credentials")) {
        setError("❌ Invalid credentials. Please try again.");
        toast.error("❌ Invalid credentials. Please try again.");
      } else {
        const message = detail || err.message || "Something went wrong.";
        setError(`❌ ${message}`);
        toast.error(`❌ ${message}`);
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.75)),
          url(${isMobile ? image2 : image1})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* LEFT CONTENT - Desktop Only */}
      {!isMobile && (
        <Box
          sx={{
            width: "600px",
            color: "#fff",
            mr: 6,
          }}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ fontFamily: "Poppins" }}
          >
            WELCOME TO
          </Typography>

          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mt: 1, color: "#f9a51c" }}
          >
            CRANOIST MHE PVT. LTD.
          </Typography>

          <Typography sx={{ mt: 2, color: "#ddd", lineHeight: 1.7 }}>
            Delivering world-class material handling solutions since 2006.
            We focus on quality, safety, and innovation to drive industrial
            excellence.
          </Typography>

          <Button
            href="/register"
            sx={{
              mt: 4,
              px: 4,
              py: 1.5,
              backgroundColor: "#f9a51c",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#ffb74d",
              },
            }}
          >
            Register
          </Button>
        </Box>
      )}

      {/* RIGHT LOGIN CARD */}
      <Box
        sx={{
          width: { xs: "100%", sm: "420px" },
          backgroundColor: "rgba(0, 0, 0, 0.14)",
          backdropFilter: "blur(10px)",
          borderRadius: "12px",
          p: 4,
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.07)",
        }}
      >
        <Box
          component="img"
          src={image3}
          sx={{ width: 140, display: "block", mx: "auto", mb: 2 }}
        />

        <Typography
          align="center"
          variant="h5"
          fontWeight="bold"
          color="#fff"
        >
          Login Account
        </Typography>

        {error && (
          <Typography color="error" align="center" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={inputStyle}
          />

          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={inputStyle}
          />

          <Button
            type="submit"
            fullWidth
            sx={{
              mt: 3,
              py: 1.5,
              backgroundColor: "#f9a51c",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#ffb74d" },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Login"
            )}
          </Button>
        </form>

        {/* MOBILE REGISTER LINK */}
        {isMobile && (
          <Typography align="center" sx={{ mt: 2, color: "#fff" }}>
            Don't have account?{" "}
            <a href="/register" style={{ color: "#f9a51c", textDecoration: "none" }}>
              Register
            </a>
          </Typography>
        )}

        {/* FOOTER */}
        <Typography
          align="center"
          sx={{ mt: 4, fontSize: "0.8rem", color: "#ccc" }}
        >
          Powered by{" "}
          <a
            href="https://www.yashvitech.com"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#1067e9be", textDecoration: "none" }}
          >
            Yashvitech IT Solution Pvt. Ltd.
          </a>
        </Typography>
      </Box>
    </Box>
  );
}

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    borderRadius: "6px",
    "& fieldset": { borderColor: "#aaa" },
    "&:hover fieldset": { borderColor: "#fff" },
    "&.Mui-focused fieldset": { borderColor: "#f9a51c" },
  },
  "& .MuiInputLabel-root": { color: "#ccc" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#f9a51c" },
};

export default LoginPage;